import React from "react";
import moment from "moment";
import { Form } from "@modules/formx-react";
import { useLocalStore, useObserver, Observer } from "mobx-react";
import {
  Divider,
  Grid,
  Typography,
  Paper,
  DialogActions,
  makeStyles,
  FormHelperText
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import SelectField from "@common/Select";
import { SearchSelect } from "@components/Select/SearchSelect";
import { useNavigation } from "@navigation";
import { Topbar, PrimaryButton, RegularButton } from "@components";
import {
  FormxDebugger,
  Text,
  Date,
  Select,
  DateProps,
  RadioGroup
} from "@components/formx";
import { TicketCreateStore } from "./TicketCreateStore";
import { CreateTicketData } from "./CreateTicketData";
import { CriminalRecordType, TodoInput } from "@graphql";
import { FormErrors, FormStore } from "@modules/formx";
import { educationTypeOptions, errandOptions } from "../utils";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";

export type TicketCreatePageProps = {
  itemName?: string;
  initialValues?: CreateTicketData;
  dueDateRequired?: boolean;
  descriptionRequired?: boolean;
  onCreated?: (todo: TodoInput, formValues: CreateTicketData) => void;
  onCancel?: () => void;
  disabledList?: string[];
  withCriminalRecord?: boolean;
  withReference?: boolean;
  consultantId?: string;
  dueDateProps?: Partial<DateProps>;
  customValidation?: (form: FormStore<CreateTicketData>) => FormErrors<CreateTicketData>;
  showSuccessAlert?: boolean;
};

const defaultProps: Partial<TicketCreatePageProps> = {
  disabledList: [],
  dueDateProps: {}
};

const CriminalRecordTypeOptionsAll = Object.values(CriminalRecordType);
const CriminalRecordTypeOptionsNational = [
  CriminalRecordType.HVB
  // CriminalRecordType.OTHER
];

export const TicketCreatePage: React.FC<TicketCreatePageProps> = (props) => {
  const {
    initialValues,
    dueDateRequired,
    descriptionRequired,
    withCriminalRecord,
    onCreated,
    withReference,
    consultantId,
    customValidation,
    onCancel,
    dueDateProps,
    disabledList,
    itemName,
    showSuccessAlert = true
  } = props;

  const formStore = useLocalStore(
    () =>
      new TicketCreateStore(
        initialValues,
        dueDateRequired,
        descriptionRequired,
        !!withCriminalRecord,
        onCreated,
        !!withReference,
        consultantId,
        customValidation,
        itemName,
        true
      )
  );
  const { showSuccess, showError } = useSnackbarsPipeline({});
  const navigation = useNavigation();
  const styles = useStyles({});
  const { DatePickerProps: dueDateDatePickerProps, ...dueDatePropsRest } = dueDateProps;
  const { form } = formStore;
  const isSITHSQualityItem = formStore.isSITHSQualityItem;
  const isHLRLikeQualityItem = formStore.isHLRLikeQualityItem;
  const criminalRecordOptions = formStore.isCriminalRecordNational
    ? CriminalRecordTypeOptionsNational
    : CriminalRecordTypeOptionsAll;
  const hasDescField = !(isSITHSQualityItem || isHLRLikeQualityItem);

  const handleClose = () => {
    onCancel?.();
    navigation.close();
  };

  return useObserver(() => {
    navigation.lockNavigation("ticket_create_form", form.dirty);

    return (
      <Form store={form} allowUpdates>
        <Grid container spacing={2} style={{ padding: 16, marginTop: -16 }}>
          <FormxDebugger store={form} />

          <Grid item xs={12}>
            <Topbar>
              <Typography variant="h6">{locale.tickets.createTicket}</Typography>
            </Topbar>
            <Divider style={{ margin: "8px -16px 0" }} />
          </Grid>

          <Grid item xs={12}>
            <Paper style={{ padding: 16 }}>
              <Grid style={{ position: "relative" }}>
                <Text
                  name="title"
                  label={locale.tickets.text}
                  TextFieldProps={{
                    autoFocus: true,
                    multiline: true,
                    disabled: disabledList.includes("title"),
                    rows: 1,
                    className: styles.textField
                  }}
                />
                {withCriminalRecord && (
                  <Grid item xs={6}>
                    <Select
                      label={locale.CriminalRecord}
                      name="criminalRecordType"
                      options={criminalRecordOptions.toLabelValue(
                        (t) => locale.criminalRecordType[t]
                      )}
                    />
                  </Grid>
                )}
                {hasDescField && (
                  <Text
                    name="description"
                    label={locale.ticketsProfile.Overview}
                    TextFieldProps={{
                      multiline: true,
                      disabled: disabledList.includes("description"),
                      className: styles.textField,
                      helperText: locale["Press Enter for a new line"]
                    }}
                  />
                )}
                {isSITHSQualityItem && (
                  <>
                    <RadioGroup
                      label={locale.tickets.errand}
                      name="errand"
                      options={errandOptions}
                    />
                    <Text
                      name="deliveryPlace"
                      label={locale.tickets.deliveryPlace}
                      TextFieldProps={{
                        multiline: true,
                        className: styles.textField,
                        helperText: locale["Press Enter for a new line"]
                      }}
                    />
                    <RadioGroup
                      label={locale.tickets.workInStockholm}
                      name="workInStockholm"
                      options={locale.yesNoOptions}
                    />
                  </>
                )}
                {isHLRLikeQualityItem && (
                  <>
                    <Text
                      name="perfPlace"
                      label={locale.tickets.perfPlace}
                      TextFieldProps={{
                        multiline: true,
                        className: styles.textField
                      }}
                    />
                    <Text
                      name="chargedProjects"
                      label={locale.tickets.chargedProjects}
                      TextFieldProps={{
                        multiline: true,
                        className: styles.textField
                      }}
                    />
                    <SelectField
                      name="educationType"
                      label={locale.tickets.educationType}
                      fullWidth
                      value={form.values.educationType}
                      onChange={(value) => {
                        form.setValue("educationType", value);
                      }}
                      options={educationTypeOptions}
                    />
                  </>
                )}
                {withReference && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <SelectField
                        // clearable={false}
                        name={`references`}
                        key={`references`}
                        label={locale.References}
                        fullWidth
                        value={formStore.selectedReference}
                        onChange={formStore.handleChangeReferences}
                        options={formStore.referencesOpt}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Text label={locale.Name} name={`reference.name`} />
                    </Grid>

                    <Grid item xs={4}>
                      <Text
                        label={locale.ReferencePosition}
                        name={`reference.position`}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <Text
                        label={locale.ReferenceWorkplace}
                        name={`reference.relation`}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <Text label={locale["Phone number"]} name={`reference.phone`} />
                    </Grid>

                    <Grid item xs={4}>
                      <Text label={locale.Email} name={`reference.email`} />
                    </Grid>
                  </Grid>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Date
                      name="dueDate"
                      utc
                      label={locale.tickets.dueDate}
                      {...dueDatePropsRest}
                      DatePickerProps={{
                        fullWidth: true,
                        disabled: disabledList.includes("dueDate"),
                        clearable: !!form.values.dueDate,
                        minDate: moment.utc().startOf("day").toDate(),
                        ...dueDateDatePickerProps
                      }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectField
                      fullWidth
                      name="priority"
                      label="Prioritet"
                      options={formStore.priorityOptions}
                      value={form.values.priority}
                      onChange={formStore.setPriority}
                      disabled={disabledList.includes("priority")}
                      helperText={formStore.priorityHelperText}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SearchSelect
                      label="Taggar"
                      options={formStore.tags}
                      onChange={formStore.setTag}
                      value={form.values.tags}
                      disabled={disabledList.includes("tags")}
                    />
                  </Grid>
                </Grid>
                {formStore.SITHSQualityItemInfo ? (
                  <FormHelperText error={formStore.SITHSQualityItemInfo.error}>
                    {formStore.SITHSQualityItemInfo.text}
                  </FormHelperText>
                ) : null}
                {/*<div className={styles.assignWrap}>*/}
                {/*  {hasAssignUsers && (*/}
                {/*    <>*/}
                {/*      <div className={styles.userAssignSection}>*/}
                {/*        <AssignButton*/}
                {/*          assignedUser={form.values.assignedUser}*/}
                {/*          onClick={() => formStore.setAssignedUser()}*/}
                {/*          onDelete={formStore.clearAssignedUser}*/}
                {/*          disabled={disabledList.includes("assignedUser")}*/}
                {/*        />*/}
                {/*      </div>*/}
                {/*      <span className={styles.or}>OR</span>*/}
                {/*    </>*/}
                {/*  )}*/}
                {/*  <div className={styles.teamAssignSection}>*/}
                {/*    <SelectField*/}
                {/*      fullWidth*/}
                {/*      name="Team"*/}
                {/*      label="Team"*/}
                {/*      disabled={disabledList.includes("assignedTeam")}*/}
                {/*      options={teamsOptions}*/}
                {/*      onChange={formStore.setAssignedTeam}*/}
                {/*      value={form.values.assignedTeam}*/}
                {/*    />*/}
                {/*  </div>*/}
                {/*</div>*/}
                {/*{form.values.linkedItems.length > 0 && (*/}
                {/*  <div>*/}
                {/*    {locale.Linked}:&nbsp;*/}
                {/*    <NodeRefList items={form.values.linkedItems} />*/}
                {/*  </div>*/}
                {/*)}*/}
              </Grid>

              <Divider style={{ margin: "8px -16px" }} />

              <Observer>
                {() => (
                  <DialogActions style={{ padding: "16px 0" }}>
                    <RegularButton style={{ marginLeft: "auto" }} onClick={handleClose}>
                      {locale.Cancel}
                    </RegularButton>

                    <PrimaryButton
                      disabled={!form.canSubmit}
                      loading={formStore.isSubmitting}
                      onClick={() => {
                        formStore
                          .submit()
                          .then(() => {
                            if (showSuccessAlert) {
                              showSuccess(locale.Saved);
                            }
                            navigation.unBlock("ticket_create_form");
                            navigation.close();
                          })
                          .catch((err) =>
                            showError(`Cannot save\nReason: ${err.toString()}`)
                          );
                      }}
                    >
                      {locale.Send}
                    </PrimaryButton>
                  </DialogActions>
                )}
              </Observer>
            </Paper>
          </Grid>
        </Grid>
      </Form>
    );
  });
};

const useStyles = makeStyles({
  datePicker: {
    width: "15%"
  },

  textField: {
    width: "100%",
    marginBottom: "10px"
  },

  userAssignSection: {
    marginRight: "10px",
    display: "flex",
    "& > button": {
      marginRight: "10px"
    }
  },
  or: {
    marginRight: 25
  },
  teamAssignSection: {
    display: "flex",
    width: "15%",
    paddingBottom: "20px"
  },

  assignWrap: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "15px"
  }
});

TicketCreatePage.defaultProps = defaultProps;
